import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, downloadJournalPdf } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PaperPlaneTilt, Plus, Trophy, Flame, Warning, ArrowRight, DownloadSimple } from "@phosphor-icons/react";

const TABS = [
  { id: "today", label: "Today" },
  { id: "triggers", label: "Triggers" },
  { id: "milestones", label: "Milestones" },
  { id: "entries", label: "Past entries" },
];

export default function Sober() {
  const { user } = useAuth();
  const unlocked = (user?.programs || []).includes("sober30");
  const [tab, setTab] = useState("today");
  const [today, setToday] = useState(null);
  const [content, setContent] = useState("");
  const [entries, setEntries] = useState([]);
  const [trigData, setTrigData] = useState({ categories: [], logged: [] });
  const [trig, setTrig] = useState({ category: "emotions", label: "", note: "", intensity: 5 });

  useEffect(() => {
    if (!unlocked) return;
    api.get("/sober/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/sober/journal").then((r) => setEntries(r.data)).catch(() => {});
    api.get("/sober/triggers").then((r) => setTrigData(r.data)).catch(() => {});
  }, [unlocked]);

  if (!unlocked) {
    return (
      <div className="bg-white rounded-3xl border border-border p-10 text-center soft-shadow">
        <Flame size={48} weight="duotone" className="mx-auto text-sage mb-4" />
        <h1 className="font-serif text-3xl mb-2">New Beginnings</h1>
        <p className="text-muted-foreground mb-6">30 days of sober-living support. $15.99 lifetime.</p>
        <Link to="/app/pricing" data-testid="sober-unlock"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sage text-white hover:bg-sage/90">
          Unlock the sober journal <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  const save = async () => {
    if (!content.trim() || !today) return;
    try {
      await api.post("/sober/journal", { content, day_number: today.day });
      setContent(""); toast.success("Entry saved.");
      api.get("/sober/journal").then((r) => setEntries(r.data));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const logTrigger = async () => {
    if (!trig.label.trim()) return;
    try {
      await api.post("/sober/trigger", trig);
      setTrig({ category: trig.category, label: "", note: "", intensity: 5 });
      toast.success("Trigger logged.");
      api.get("/sober/triggers").then((r) => setTrigData(r.data));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!today) return <div className="h-32" />;
  const pct = Math.round((today.day / 30) * 100);

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-2">
          New Beginnings · Day {today.day} of 30
        </p>
        <h1 className="font-serif text-4xl md:text-5xl font-medium">One day. One breath.</h1>
        <div className="relative h-3 mt-5 rounded-full bg-cream overflow-hidden max-w-md">
          <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-sage to-moss rounded-full" style={{ width: `${pct}%` }} />
        </div>
        {today.milestone && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-moss/10 text-moss">
            <Trophy size={16} weight="duotone"/> <span className="font-serif">{today.milestone.title}:</span> {today.milestone.message}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`sober-tab-${t.id}`}
                  className={`px-4 py-2 -mb-px text-sm border-b-2 transition-colors ${
                    tab === t.id ? "border-sage text-sage" : "border-transparent text-muted-foreground hover:text-charcoal"
                  }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "today" && (
        <div className="space-y-6">
          <div className="relative bg-white rounded-3xl border border-border p-8 md:p-10 soft-shadow overflow-hidden">
            <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-sage/15 blur-3xl"/>
            <div className="relative">
              <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4">Today's prompt</p>
              <p className="font-serif text-2xl md:text-3xl leading-snug mb-5" data-testid="sober-prompt">{today.prompt}</p>
              <textarea value={content} onChange={(e) => setContent(e.target.value)}
                        data-testid="sober-textarea" placeholder="Write what's true today — even one line counts."
                        className="w-full min-h-[200px] rounded-2xl border border-border bg-sand p-5 focus:outline-none focus:ring-2 focus:ring-sage"/>
              <div className="flex justify-end mt-4">
                <button onClick={save} disabled={!content.trim()} data-testid="sober-save"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sage text-white hover:bg-sage/90 disabled:opacity-50">
                  <PaperPlaneTilt size={16}/> Save entry
                </button>
              </div>
            </div>
          </div>
          <div className="bg-cream/60 rounded-3xl border border-border p-6 italic font-serif text-lg" data-testid="sober-quote">
            "{today.quote}"
          </div>
        </div>
      )}

      {tab === "triggers" && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl border border-border p-6 md:p-8 soft-shadow space-y-3">
            <div className="flex flex-wrap gap-2">
              {trigData.categories.map((c) => (
                <button key={c.key} onClick={() => setTrig({ ...trig, category: c.key })}
                        data-testid={`trigger-cat-${c.key}`}
                        className={`px-4 py-2 rounded-full text-sm border ${
                          trig.category === c.key ? "bg-sage text-white border-transparent" : "border-border hover:bg-cream"
                        }`}>
                  {c.label}
                </button>
              ))}
            </div>
            <input value={trig.label} onChange={(e) => setTrig({ ...trig, label: e.target.value })}
                   placeholder="What triggered you? (e.g. Old route home)"
                   data-testid="trigger-label"
                   className="w-full px-4 py-2.5 rounded-full border border-border bg-sand focus:outline-none focus:ring-2 focus:ring-sage"/>
            <textarea value={trig.note} onChange={(e) => setTrig({ ...trig, note: e.target.value })}
                      placeholder="How did you handle it? What did you do instead?"
                      data-testid="trigger-note"
                      className="w-full min-h-[80px] rounded-2xl border border-border bg-sand p-4 focus:outline-none focus:ring-2 focus:ring-sage"/>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-sage font-bold">Intensity · {trig.intensity}/10</label>
              <input type="range" min="1" max="10" value={trig.intensity}
                     onChange={(e) => setTrig({ ...trig, intensity: parseInt(e.target.value) })}
                     className="w-full mt-2 accent-sage"/>
            </div>
            <div className="flex justify-end">
              <button onClick={logTrigger} data-testid="trigger-save"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sage text-white hover:bg-sage/90">
                <Plus size={16}/> Log trigger
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {trigData.logged.length === 0 ? <p className="text-muted-foreground">No triggers logged yet — that's okay. They're just data.</p>
              : trigData.logged.map((t) => (
                <div key={t.id} className="bg-white rounded-2xl border border-border p-4 flex items-start gap-3">
                  <Warning size={18} weight="duotone" className="text-clay mt-0.5"/>
                  <div className="flex-1">
                    <p className="font-medium">{t.label} <span className="text-xs text-muted-foreground">· {t.category} · {t.intensity}/10</span></p>
                    {t.note && <p className="text-sm text-muted-foreground">{t.note}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{new Date(t.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {tab === "milestones" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(today.milestones || []).map((m) => {
            const reached = today.day >= m.day;
            return (
              <div key={m.day} data-testid={`milestone-${m.day}`}
                   className={`rounded-3xl border p-6 transition-all ${
                     reached ? "bg-moss text-white border-transparent" : "bg-white border-border"
                   }`}>
                <div className="flex items-center gap-2 mb-2">
                  <Trophy size={18} weight="duotone" className={reached ? "text-white" : "text-muted-foreground"}/>
                  <span className={`text-xs tracking-[0.2em] uppercase font-bold ${reached ? "text-white/80" : "text-sage"}`}>
                    Day {m.day}
                  </span>
                </div>
                <h3 className="font-serif text-2xl mb-1">{m.title}</h3>
                <p className={reached ? "text-white/85" : "text-muted-foreground"}>{m.message}</p>
              </div>
            );
          })}
        </div>
      )}

      {tab === "entries" && (
        <div className="space-y-3">
          {entries.length > 0 && (
            <div className="flex justify-end">
              <button onClick={() => downloadJournalPdf("sober30")} data-testid="sober-export-pdf"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-white hover:bg-cream text-sm">
                <DownloadSimple size={14}/> Export PDF
              </button>
            </div>
          )}
          {entries.length === 0 ? <p className="text-muted-foreground">Your entries will appear here.</p>
            : entries.map((e) => (
              <div key={e.id} className="bg-white rounded-2xl border border-border p-5 soft-shadow">
                <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-2">
                  Day {e.day_number} · {new Date(e.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </p>
                <p className="whitespace-pre-wrap leading-relaxed">{e.content}</p>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
